import { useState, useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react'

function Toast({ message, type = 'success', duration, onClose, index = 0, action }) {
  const [isVisible, setIsVisible] = useState(false)
  const [progress, setProgress] = useState(100)

  // Default durations based on type
  const defaultDurations = {
    success: 3000,
    info: 4000,
    warning: 5000,
    error: 6000
  }
  
  const toastDuration = duration || defaultDurations[type] || 3000

  useEffect(() => {
    // Slide in animation
    setTimeout(() => setIsVisible(true), 10)

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const decrement = (100 / toastDuration) * 50 // Update every 50ms
        return Math.max(0, prev - decrement)
      })
    }, 50)

    // Auto close after duration
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(onClose, 300) // Wait for slide out animation
    }, toastDuration)

    return () => {
      clearTimeout(timer)
      clearInterval(progressInterval)
    }
  }, [toastDuration, onClose])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 300)
  }

  const icons = {
    success: <CheckCircle className="h-5 w-5 text-success" />,
    error: <AlertCircle className="h-5 w-5 text-destructive" />,
    warning: <AlertTriangle className="h-5 w-5 text-warning" />,
    info: <Info className="h-5 w-5 text-info" />
  }

  // Calculate vertical offset for stacking (each toast is offset by 70px)
  const verticalOffset = index * 70

  return (
    <div
      className={`fixed left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-out ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
      style={{ bottom: `${24 + verticalOffset}px` }}
    >
      <div className="toast-gradient relative flex flex-col gap-2 px-4 py-3 rounded-lg min-w-[320px] max-w-md overflow-hidden">
        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-lg">
          <div 
            className="h-full bg-[var(--color-ink)] opacity-15 transition-all duration-50 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Content */}
        <div className="flex items-center gap-3">
          {icons[type]}
          <p className="flex-1 text-sm font-medium text-[var(--color-ink)]">{message}</p>
          {action && (
            <button
              onClick={() => {
                action.onClick()
                handleClose()
              }}
              className="text-sm font-semibold text-[var(--color-ink)] hover:opacity-70 transition-opacity"
            >
              {action.label}
            </button>
          )}
          <button
            onClick={handleClose}
            className="text-[var(--color-ink)] opacity-50 hover:opacity-80 transition-opacity"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export function useToast() {
  const [toasts, setToasts] = useState([])
  const MAX_TOASTS = 3 // Maximum number of visible toasts

  const showToast = (message, type = 'success', options = {}) => {
    const id = Date.now() + Math.random() // Ensure unique IDs
    const newToast = { 
      id, 
      message, 
      type, 
      duration: options.duration,
      action: options.action
    }
    
    setToasts(prev => {
      // If we're at max, remove the oldest toast
      const updatedToasts = prev.length >= MAX_TOASTS 
        ? [...prev.slice(1), newToast]
        : [...prev, newToast]
      return updatedToasts
    })
    
    return id
  }

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }

  const ToastContainer = () => (
    <>
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          action={toast.action}
          index={index}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </>
  )

  return { showToast, ToastContainer }
}
